import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
import { onlineExhibit } from '@/utils/nodeTools';
import { fOnOffFeedback } from '@/components/fOnOffFeedback';
import { listStateAndListMore } from '@/components/FListFooter';
import type {
  NodeManagerModelState,
  ChangeAction,
  OnMount_ExhibitPage_Action,
  OnUnmount_ExhibitPage_Action,
  OnChange_Exhibit_SelectedType_Action,
  OnChange_Exhibit_SelectedStatus_Action,
  OnChange_Exhibit_InputFilter_Action,
  OnLoadMore_ExhibitList_Action,
  OnOnlineOrOfflineAction,
  FetchExhibitsAction,
} from './types';
import { exhibitInitStates } from './initialState';
import dayjs from 'dayjs';

export const exhibitEffects = {
  *onMount_ExhibitPage({}: OnMount_ExhibitPage_Action, { select, put }: EffectsCommandMap) {
    yield put<FetchExhibitsAction>({
      type: 'fetchExhibits',
      payload: {
        restart: true,
      },
    });
  },
  *onUnmount_ExhibitPage({}: OnUnmount_ExhibitPage_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        ...exhibitInitStates,
      },
    });
  },
  *onChange_Exhibit_SelectedType(
    { payload }: OnChange_Exhibit_SelectedType_Action,
    { put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_ResourceTypeCodes: payload.value,
      },
    });

    yield put<FetchExhibitsAction>({
      type: 'fetchExhibits',
      payload: {
        restart: true,
      },
    });
  },
  *onChange_Exhibit_SelectedStatus(
    { payload }: OnChange_Exhibit_SelectedStatus_Action,
    { put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_SelectedStatus: payload.value,
      },
    });

    yield put<FetchExhibitsAction>({
      type: 'fetchExhibits',
      payload: {
        restart: true,
      },
    });
  },
  *onChange_Exhibit_InputFilter(
    { payload }: OnChange_Exhibit_InputFilter_Action,
    { put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_InputFilter: payload.value,
      },
    });
  },
  *onLoadMore_ExhibitList({}: OnLoadMore_ExhibitList_Action, { put }: EffectsCommandMap) {
    yield put<FetchExhibitsAction>({
      type: 'fetchExhibits',
      payload: {
        restart: false,
      },
    });
  },

  *onOnlineOrOffline(
    { payload }: OnOnlineOrOfflineAction,
    { call, put, select }: EffectsCommandMap,
  ) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );

    if (payload.onlineStatus === 1) {
      const result: boolean = yield call(onlineExhibit, {
        exhibit_ID: payload.id,
      });
      if (!result) {
        return;
      }
    } else {
      const params2: Parameters<typeof FServiceAPI.Exhibit.presentablesOnlineStatus>[0] = {
        presentableId: payload.id,
        onlineStatus: 0,
      };
      yield call(FServiceAPI.Exhibit.presentablesOnlineStatus, params2);
      fOnOffFeedback({
        state: 'off',
        message: FI18n.i18nNext.t('remove_resource_from_auth_msg_done'),
      });
    }

    const params: Parameters<typeof FServiceAPI.Exhibit.presentableDetails>[0] = {
      presentableId: payload.id,
      isLoadPolicyInfo: 1,
    };

    const { data: data_exhibit } = yield call(FServiceAPI.Exhibit.presentableDetails, params);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_List: nodeManagerPage.exhibit_List
          .map((el) => {
            if (payload.id !== el.id) {
              return el;
            }
            return {
              ...el,
              isOnline: payload.onlineStatus === 1,
              policiesList: data_exhibit.policies.reverse(),
              policies: (data_exhibit.policies as any[])
                .filter((p: any) => p.status === 1)
                .map<string>((p) => p.policyName),
              hasPolicy: data_exhibit.policies.length > 0,
            };
          })
          .filter((el) => {
            if (nodeManagerPage.exhibit_SelectedStatus === '2') {
              return true;
            }
            return el.isOnline === (nodeManagerPage.exhibit_SelectedStatus === '1');
          }),
      },
    });
  },

  *fetchExhibits({ payload }: FetchExhibitsAction, { call, select, put }: EffectsCommandMap) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );

    const oldList: NodeManagerModelState['exhibit_List'] = payload.restart
      ? []
      : nodeManagerPage.exhibit_List;
    if (payload.restart) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_ListState: 'loading',
        },
      });
    } else {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_ListMore: 'loading',
        },
      });
    }

    const resourceTypes: string[] = nodeManagerPage.exhibit_ResourceTypeCodes.value.split('#');
    let resourceTypeCode: string = resourceTypes[0];
    let resourceType: string = '';

    if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'other') {
      resourceTypeCode = resourceTypes[0];
    }

    if (resourceTypes.length > 1 && resourceTypes[resourceTypes.length - 1] === 'all') {
      resourceType =
        nodeManagerPage.exhibit_ResourceTypeCodes.labels[
          nodeManagerPage.exhibit_ResourceTypeCodes.labels.length - 2
        ];
    }

    const params: Parameters<typeof FServiceAPI.Exhibit.presentables>[0] = {
      nodeId: nodeManagerPage.nodeId,
      limit: FUtil.Predefined.pageSize,
      skip: oldList.length,
      keywords: nodeManagerPage.exhibit_InputFilter || undefined,
      onlineStatus: Number(nodeManagerPage.exhibit_SelectedStatus),
      resourceType: resourceType || undefined,
      resourceTypeCode: resourceType === '' ? resourceTypeCode || undefined : undefined,
      omitResourceType: '主题',
      isLoadPolicyInfo: 1,
      isLoadResourceDetailInfo: 1,
      isLoadVersionProperty: 1,
      isLoadVersionUpdateTip: 1,
    };
    const {
      data: data_Exhibits,
    }: {
      data: {
        dataList: {
          presentableId: string;
          presentableIntro: string;
          presentableName: string;
          presentableTitle: string;
          coverImages: string[];
          version: string;
          onlineStatus: 0 | 1;
          resourceInfo: {
            resourceId: string;
            resourceType: string[];
            resourceTypeCode: string;
            serializeStatus: 0 | 1;
            resourceVersions: {
              versionId: string;
            }[];
            latestVersion: string;
          };
          policies: any[];
          versionUpdateTipStatus: 1 | 2 | 3;
          autoUpdateStatus: 0 | 1;
          subjectType: 1 | 4 | 5;
          versionProperty: {
            catalogueProperty?: {
              collection_item_count: number;
            };
          };
        }[];
        totalItem: number;
      };
    } = yield call(FServiceAPI.Exhibit.presentables, params);

    /** 普通合集展品 Start **************************/
    const collectionExhibits = data_Exhibits.dataList.filter((d) => {
      return d.subjectType === 4;
    });

    let collectionExhibits_versionList: {
      resourceId: string;
      catalogueProperty: { collection_item_count: number };
      updateDate: string;
    }[] = [];

    let collectionExhibits_batchResourceItems: {
      resourceId: string;
      itemList: {
        itemTitle: string;
      }[];
    }[] = [];

    if (collectionExhibits.length > 0) {
      const versionIds = collectionExhibits
        .map((bi) => bi.resourceInfo?.resourceVersions?.[0]?.versionId)
        .filter(Boolean)
        .join(',');

      if (versionIds) {
        const params2: Parameters<typeof FServiceAPI.Resource.getVersionList>[0] = {
          versionIds,
        };
        const {
          data: data_versionList,
        }: {
          data: {
            resourceId: string;
            catalogueProperty: { collection_item_count: number };
            updateDate: string;
          }[];
        } = yield call(FServiceAPI.Resource.getVersionList, params2);
        collectionExhibits_versionList = data_versionList || [];
      }

      const resourceIds = collectionExhibits
        .map((ce) => ce.resourceInfo?.resourceId)
        .filter(Boolean)
        .join(',');

      if (resourceIds) {
        const params3: Parameters<typeof FServiceAPI.Resource.batchResourceItems>[0] = {
          resourceIds,
          limit: 1,
          sortField: 'createDate',
          sortType: -1,
        };
        const {
          data: data_batchResourceItems,
        }: {
          data: {
            resourceId: string;
            itemList: {
              itemTitle: string;
            }[];
          }[];
        } = yield call(FServiceAPI.Resource.batchResourceItems, params3);
        collectionExhibits_batchResourceItems = data_batchResourceItems || [];
      }
    }

    /** 普通合集展品 End **************************/

    /** 创建合集展品 Start **************************/
    const createdCollectionExhibits = data_Exhibits.dataList.filter((d) => {
      return d.subjectType === 5;
    });

    let data_getItemsFromPresentableCollections: {
      presentableId: string;
      totalItem: number;
      itemList: {
        itemId: string;
        mountPresentableInfo: {
          presentableName: string;
          presentableTitle: string;
        };
        createDate: string;
      }[];
    }[] = [];

    if (createdCollectionExhibits.length > 0) {
      const params5: Parameters<
        typeof FServiceAPI.Exhibit.getItemsFromPresentableCollections
      >[0] = {
        presentableIds: createdCollectionExhibits
          .map((d) => {
            return d.presentableId;
          })
          .join(','),
        limit: 1,
        sortField: 'createDate',
        sortType: -1,
      };
      const {
        data,
      }: {
        data: {
          presentableId: string;
          totalItem: number;
          itemList: {
            itemId: string;
            mountPresentableInfo: {
              presentableName: string;
              presentableTitle: string;
            };
            createDate: string;
          }[];
        }[];
      } = yield call(FServiceAPI.Exhibit.getItemsFromPresentableCollections, params5);
      data_getItemsFromPresentableCollections = data || [];
    }

    /** 创建合集展品 End **************************/

    const exhibit_List: NodeManagerModelState['exhibit_List'] = [
      ...oldList,
      ...data_Exhibits.dataList.map<NodeManagerModelState['exhibit_List'][number]>((i) => {
        const versionInfo = collectionExhibits_versionList.find((vl) => {
          return vl?.resourceId === i.resourceInfo?.resourceId;
        });
        const itemInfo = collectionExhibits_batchResourceItems.find((bri) => {
          return bri?.resourceId === i.resourceInfo?.resourceId;
        });

        const createdCollectionExhibit:
          | {
              presentableId: string;
              totalItem: number;
              itemList: {
                itemId: string;
                createDate: string;
                mountPresentableInfo: {
                  presentableName: string;
                  presentableTitle: string;
                };
              }[];
            }
          | undefined = data_getItemsFromPresentableCollections.find((d) => {
          return d.presentableId === i.presentableId;
        });

        return {
          id: i.presentableId,
          cover: i.coverImages?.[0] ?? '',
          title: i.presentableTitle || i.presentableName,
          resourceName: i.presentableName,
          version: i.version,
          isOnline: i.onlineStatus === 1,
          type: i.resourceInfo?.resourceType || [],
          resourceTypeCode: i.resourceInfo?.resourceTypeCode || '',
          resourceLatestVersion: i.resourceInfo?.latestVersion || '',
          policiesList: i.policies.reverse(),
          policies: i.policies
            .filter((p) => {
              return p.status === 1;
            })
            .map<string>((p) => {
              return p.policyName;
            }),
          hasPolicy: i.policies.length > 0,
          resourceId: i.resourceInfo?.resourceId || '',
          isAuth: true,
          authErrorText: '',
          collection:
            i.subjectType === 4
              ? {
                  status: i.resourceInfo?.serializeStatus === 1 ? 'finish' : 'unFinish',
                  count: i.versionProperty?.catalogueProperty?.collection_item_count ?? 0,
                  items:
                    itemInfo?.itemList.map((il) => {
                      return il.itemTitle || '需要标题';
                    }) ?? [],
                  latestUpdateTime: versionInfo
                    ? dayjs(versionInfo.updateDate).format(FUtil.Predefined.momentDateFormat)
                    : 'YYYY-MM-DD',
                }
              : null,
          createdCollectionExhibit:
            i.subjectType === 5
              ? {
                  count: createdCollectionExhibit?.totalItem ?? 0,
                  items:
                    createdCollectionExhibit?.itemList?.map((il) => {
                      return il.mountPresentableInfo?.presentableTitle || '###需要标题###';
                    }) ?? [],
                  latestUpdateTime: dayjs(
                    createdCollectionExhibit?.itemList?.[0]?.createDate ?? '',
                  ).format(FUtil.Predefined.momentDateFormat),
                }
              : null,
          hasUpdateVersion:
            i.versionUpdateTipStatus === 1
              ? 'noUpdate'
              : i.versionUpdateTipStatus === 2
              ? 'hasUpdate'
              : 'ignored',
          autoUpdate: i.autoUpdateStatus === 1,
        };
      }),
    ];

    const { state, more } = listStateAndListMore({
      list_Length: exhibit_List.length,
      total_Length: data_Exhibits.totalItem,
      has_FilterCriteria: !(
        nodeManagerPage.exhibit_ResourceTypeCodes.values[0] === '#all' &&
        nodeManagerPage.exhibit_SelectedStatus === '2' &&
        nodeManagerPage.exhibit_InputFilter === ''
      ),
    });

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_List: exhibit_List,
        exhibit_ListTotal: data_Exhibits.totalItem,
        exhibit_ListState: state,
        exhibit_ListMore: more,
      },
    });

    let batchAuthPs: {
      error: string;
      isAuth: boolean;
      presentableId: string;
    }[] = [];
    if (data_Exhibits.dataList.length > 0) {
      const params1: Parameters<typeof FServiceAPI.Exhibit.batchAuth>[0] = {
        nodeId: nodeManagerPage.nodeId,
        authType: 3,
        presentableIds: data_Exhibits.dataList
          .map<string>((dl: any) => {
            return dl.presentableId;
          })
          .join(','),
      };
      const {
        data: data1,
      }: {
        data: {
          error: string;
          isAuth: boolean;
          presentableId: string;
        }[];
      } = yield call(FServiceAPI.Exhibit.batchAuth, params1);
      batchAuthPs = data1;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_List: exhibit_List.map((i) => {
          const authInfo = batchAuthPs.find((bap) => {
            return bap.presentableId === i.id;
          });

          if (!authInfo) {
            return i;
          }

          return {
            ...i,
            isAuth: authInfo.isAuth,
            authErrorText: authInfo.error,
          };
        }),
      },
    });
  },
};
